/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FIPApiService, Layer4ApiService, Layer7ApiService } from '../../http';
import { FipModel, L7VirtualServiceModel } from '../../models';
import { LogService, RbacService, UtilsService } from '../../../../../core/services';
import { MetricsEnum } from '../../enums';
import { WidgetBaseComponent } from '../widgetBase.component';
import { WidgetInterface } from '../../interfaces/widget.interface';
import { WidgetService } from '../../services/utilities/widget.service';
import { WidgetStateEnum } from '../../../../../core/enums';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-widget-virtual-services',
    templateUrl: './virtualServices.widget.html',
    styleUrls: ['../../../../../core/components/widget/widget.component.scss']
})

export class VirtualServicesWidgetComponent extends WidgetBaseComponent implements WidgetInterface, AfterViewInit {
    /**
     * Get widget table container.
     */
    @ViewChild('list', { static: true }) public list: ElementRef;

    /**
     * Metric input e.g connections
     */
    @Input() public metric: string;

    /**
     * A list of all of our servicesFrontend.
     */
    private frontendServices: L7VirtualServiceModel[];

    /**
     * List of our FIPS.
     */
    private fips: FipModel[];

    /**
     * Set our default state.
     */
    public state: WidgetStateEnum;

    /**
     * To store the number of virtual services that are online.
     */
    public online: number;
    public onlineStatus: string = 'N/A';

    /**
     * To store the number of virtual services that are in maintenance.
     */
    public maint: number;
    public maintStatus: string = 'N/A';

    /**
     * To store the number of virtual services that have failed.
     */
    public failure: number;
    public down: number;
    public downStatus: string = 'N/A';

    /**
     * Socket loading indicator.
     */
    public loading: boolean;

    /**
     * Api loading indicator.
     */
    public apiLoading: boolean = false;

    /**
     * Determine whether to show a larger list or not.
     */
    public scrolled: boolean;

    /**
     * Table data to show in the widget.
     */
    public serviceData: any[] = [];

    /**
     * Store permissions for buttons.
     */
    public permissionsLookup: any = {};

    /**
     * Store socket data for reference.
     * @private
     */
    private socketData: any = {};

    /**
     * Store total frontend connections.
     * @private
     */
    private _totalConnections: number = 0;

    constructor(
        private fipApiService: FIPApiService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private widgetService: WidgetService,
        utils: UtilsService,
    ) {
        super(utils);
        this.resetLocalVariables();
        this.registerButtons();
    }

    /**
     * Setter/getter for total frontend connections.
     * @param totalConnections
     */
    set totalConnections(totalConnections: number) {
        this._totalConnections = totalConnections;
    }

    get totalConnections(): number {
        return this._totalConnections;
    }

    public ngAfterViewInit(): void {
        const element = this.list.nativeElement;
        if (element) {
            element.addEventListener('scroll', (e: { target: { scrollTop: number; }; }) => {
                if (e.target.scrollTop >= 11) {
                    this.scrolled = true;
                }
            });
        }
    }

    /**
     * Navigate to manage services page.
     * @param filter
     */
    public navigateToManageServicesPage(filter: any): void {
        if (this.permissionsLookup['virtual-services-manage-services']) {
            this.router.navigate(['../cluster-configuration/manage-services'], {
                queryParams: { sFilter: filter },
                relativeTo: this.route
            }).then();
        }
    }

    /**
     * Navigate to a servicesFrontend details page.
     * @param row
     */
    public navigateToServiceDetailsPage(row: any): void {
        if (this.permissionsLookup['virtual-services-service-details']) {
            const url = ['../cluster-configuration/manage-services/service-details', row.templateType, row.id];
            this.router.navigate(url, { relativeTo: this.route })
                .then()
                .catch();
        }
    }

    /**
     * Set up the data for the widget.
     * @private
     */
    public getWidgetDetails(contentEnabled: boolean): void {
        if (contentEnabled && this.permissionsLookup['virtual-services-manage-services'] && this.metric !== 'connections') {
            const apiCalls = {
                fips: this.fipApiService.getFloatingIPs(),
                frontends4: this.layer4ApiService.getFrontends(),
                frontends7: this.layer7ApiService.getFrontends(),
            };

            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        if (data['frontends7']) {
                            this.utils.addTypeToService(data['frontends7'], 'layer7', 'Layer 7');
                        }

                        if (data['frontends4']) {
                            this.utils.addTypeToService(data['frontends4'], 'layer4', 'Layer 4');
                        }

                        this.fips = data['fips'];
                        this.frontendServices = data['frontends4'].concat(data['frontends7']);
                        this.socketListeners();
                        this.setTableData(this.frontendServices);

                        this.apiLoading = false;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('VirtualServicesWidget:setUpWidgetData', error.message);
                    }
                });
        } else {
            this.socketListeners();
        }
    }

    /**
     * Get total connections for all frontends combined.
     * @param frontends
     * @private
     */
    private getTotalConnections(frontends: any): number {
        let totalConnections: number = 0;
        if (frontends?.length) {
            for (const frontend of frontends) {
                totalConnections += frontend.connection.connections;
            }
        }

        return totalConnections;
    }

    /**
     * Set up the socket listeners.
     * @private
     */
    public socketListeners(): void {
        this.widgetService.haDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.setStateAndConnections(response);
                }
            });

        this.state = WidgetStateEnum.WARNING;

        this.widgetService.socketClosedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (hasClosed): void => {
                    if (hasClosed) {
                        this.state = WidgetStateEnum.WARNING;
                    }
                }
            });

        this.widgetService.sendSubscriptionConfirmEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (service): void => {
                    if (service === 'haproxy') {
                        this.widgetService.receiveSubscriptionConfirmEmitter.next({
                            service: 'haproxy',
                            event: 'stats'
                        });
                    }
                }
            });
    }

    /**
     * Take the websocket response and set the states and connections.
     * @param response
     * @private
     */
    private setStateAndConnections(response: any): void {
        this.state = response ? WidgetStateEnum.OK : this.state;

        // We are no longer loading the socket widgets
        if (this.loading) {
            this.loading = false;
        }

        if (JSON.stringify(this.socketData) !== JSON.stringify(response)) {
            this.loading = true;
            this.socketData = response;
            this.totalConnections = this.getTotalConnections(response['frontends']);
            this.calculateTotalStates(response['frontends']);
            this.loading = false;
        }
    }

    /**
     * Calculate total statuses for the frontends.
     * @param frontends
     * @private
     */
    private calculateTotalStates(frontends: any): void {
        this.resetLocalVariables();
        for (const frontend of frontends) {
            switch (frontend.status.toLowerCase()) {
                case 'online':
                    this.online++;
                    break;
                case 'warning':
                case 'maintenance':
                    this.maint++;
                    this.maintStatus = 'warning, maintenance';
                    break;
                case 'down':
                case 'failure':
                    this.failure++;
                    this.downStatus = 'down, failure';
                    break;
            }
        }
    }

    /**
     * Set the data to display in the services table.
     * @param services
     * @private
     */
    private setTableData(services: L7VirtualServiceModel[]): void {
        for (const service of services) {
            this.serviceData.push({
                id: service.id,
                label: service.name,
                fip: this.getFloatingIPByID(service.fipId),
                sessions: 0, // This is set to zero until the bear has been fixed.
                status: 'n/a', // as above.
                SSLEnable: service.SSLEnable,
                ports: service.ports,
                serviceType: service.type,
                // @ts-ignore
                templateType: service.templateType,
                // @ts-ignore
                templateTypeFriendlyName: service.templateTypeFriendlyName, // TODO to be updated when layer 4 and GSLB is added
            });
        }
    }

    /**
     * Get floating IP by FIP ID.
     * @param id
     * @private
     */
    private getFloatingIPByID(id: string): string {
        for (const fip of this.fips) {
            if (fip.id === id) {
                return fip.ip;
            }
        }

        return 'N/A';
    }

    /**
     * Reset the local variables.
     * @private
     */
    public resetLocalVariables(): void {
        this.online = 0;
        this.failure = 0;
        this.maint = 0;
        this.frontendServices = [];
        this.fips = [];
        this.state = WidgetStateEnum.OK;
        this.loading = true;
        this.apiLoading = true;
    }

    /**
     * Register button permissions
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'virtual-services-manage-services': this.getManageServicesPermissions(),
            'virtual-services-service-details': this.getServiceDetailsPermissions()
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Get permissions to access manage services page.
     * @private
     */
    private getManageServicesPermissions(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] }
        ];
    }

    /**
     * Get permissions to access service details page.
     * @private
     */
    private getServiceDetailsPermissions(): any[] {
        return [
            { path: '/haproxy/frontends/:id', methods: ['GET'] },
            { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] },
        ];
    }

    protected readonly MetricsEnum = MetricsEnum;
    protected readonly WidgetStateEnum = WidgetStateEnum;
}
